import { useEffect, useMemo, useState, useRef } from "react";
import styles from "./BulkUers.module.css";
import { Pagination } from "antd";
import * as XLSX from "xlsx";
import BulkNortification from "./Notification/BulkNotification";
import axios from "axios";
import store from "../../../Store/Store";
import config from "../../../config/config";
import { useNavigate } from "react-router";
import { jwtDecode } from "jwt-decode";
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { verifyTokenAccess } from "../../../Helper";

const BulkUsers = () => {
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState(false);
  const [incompleteRows, setIncompleteRows] = useState([]);
  const [completeData, setCompleteData] = useState([]);
  const [fullData, setFullData] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [duplicateUsers, setDuplicateUsers] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [dataUpdated, setDataUpdated] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const allowedTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ];

  const downloadExcel = () => {
    const s3FileUrl =
      "https://my-email-assets-escrowstack.s3.ap-south-1.amazonaws.com/New_Bulk_user.xlsx";

    // Create a temporary anchor element
    const link = document.createElement("a");
    link.href = s3FileUrl;
    link.download = "Bulk_user_import.xlsx"; // Optional: Specify the file name for download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    console.log("completeData", completeData);

    const newData = [...completeData, ...incompleteRows];
    setFullData(newData);
  }, [completeData, incompleteRows]);

  const requiredFields = [
    "user_name",
    "user_mobile_no",
    "perTxnLimit",
    "dailyLimit",
    "monthlyLimit",
    "isAdmin",
  ];
  const optionalFields = ["tags", "user_email"];

  const isValidEmail = (email) => {
    if (!email) return true;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };
  
  const isValidMobileNo = (mobile) => {
    const mobileRegex = /^\d{10}$/;
    return mobileRegex.test(mobile);
  };

  const isValidUsername = (username) => {
    return username && username.length >= 5;
  };

  const isValidLimits = (perTxnLimit, dailyLimit, monthlyLimit) => {
    const perTxn = Number(perTxnLimit);
    const daily = Number(dailyLimit);
    const monthly = Number(monthlyLimit);

    return {
      perTxnValid: !isNaN(perTxn) && perTxn > 0,
      dailyValid: !isNaN(daily) && daily > 0,
      monthlyValid: !isNaN(monthly) && monthly > 0,
      perTxnVsDaily: perTxn <= daily,
      dailyVsMonthly: daily <= monthly,
      perTxnVsMonthly: perTxn <= monthly,
      perTxnLimit: perTxn <= 100000,
      dailyLimit: daily <= 100000,
    };
  };

  const checkDuplicates = (data) => {
    const phoneCounts = {};
    const emailCounts = {};
    const duplicates = [];

    data.forEach((row) => {
      const phone = row.user_mobile_no.replace("+91", "");

      phoneCounts[phone] = (phoneCounts[phone] || 0) + 1;

      if (phoneCounts[phone] > 1) {
        duplicates.push({
          ...row,
          issues: ["Duplicate Entries"],
        });
      }
    });

    return duplicates;
  };

  const validateUser = (user) => {
    const issues = [];

    // Mandatory fields
    if (!user.user_name || !user.user_mobile_no || user.isAdmin === undefined) {
      issues.push("Missing Entries");
    }

    // Username
    if (!isValidUsername(user.user_name)) {
      issues.push("user_name: Must be at least 5 characters");
    }

    // Email format
    if (user.user_email && !isValidEmail(user.user_email)) {
      issues.push("Wrong Email Format");
    }

    // Phone number format
    const cleanMobile = user.user_mobile_no.replace("+91", "");
    if (!isValidMobileNo(cleanMobile)) {
      issues.push("Wrong Ph num Format");
    }

    // Transaction limits
    const limitsValidation = isValidLimits(
      user.perTxnLimit,
      user.dailyLimit,
      user.monthlyLimit
    );
    if (!limitsValidation.perTxnValid) {
      issues.push("Txn limit cant be empty or 0");
    }
    if (!limitsValidation.dailyValid) {
      issues.push("Daily limit cant be empty or 0");
    }
    if (!limitsValidation.monthlyValid) {
      issues.push("Monthly limit cant be empty or 0");
    }
    if (!limitsValidation.perTxnVsDaily) {
      issues.push("Per txn cant be more than Daily");
    }
    if (!limitsValidation.dailyVsMonthly) {
      issues.push("Daily txn cant be more than Monthly");
    }
    if (!limitsValidation.perTxnVsMonthly) {
      issues.push("Per txn cant be more than Monthly");
    }

    if (!limitsValidation.perTxnLimit && store.Company_Id !== "AxisDemo") {
      issues.push("Per txn limit exceeds 1 lakh");
    }
    if (!limitsValidation.dailyLimit && store.Company_Id !== "AxisDemo") {
      issues.push("Daily txn limit exceeds 1 lakh");
    }

    // Admin role
    if (user.isAdmin && !store.isUserOwner) {
      issues.push("You are not authorised to add Admin users.");
    }

    return issues;
  };

  const handleFileUpload = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) {
        console.log("No file selected");
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        console.log("Invalid file type:", file.type);
        setError(true);
        setIncompleteRows([]);
        setCompleteData([]);
        setFileName("");
        setTotalRows(0);
        return;
      }

      setFileName(file.name);
      setError(false);

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // Parse the file
          let data;
          if (file.type === "text/csv") {
            data = XLSX.read(e.target.result, { type: "string" });
          } else {
            data = XLSX.read(new Uint8Array(e.target.result), {
              type: "array",
            });
          }

          const worksheet = data.Sheets[data.SheetNames[0]];
          if (!worksheet) {
            console.log("No worksheet found");
            setError(true);
            return;
          }

          const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
          console.log("Worksheet range:", range);

          // Define column-to-field mapping
          const columnMap = {
            0: "User Name",
            1: "User Email",
            2: "Per Transaction Limit",
            3: "Daily Limit",
            4: "Monthly Limit",
            5: "User Mobile Number",
            6: "isAdmin",
            7: "Tags",
          };

          // Extract rows starting from row 1 (skip header row)
          const allRows = [];
          for (let rowNum = 1; rowNum <= range.e.r; rowNum++) {
            const rowData = { rowNumber: rowNum + 1 };
            let hasData = false;

            // Extract data based on column index
            Object.keys(columnMap).forEach((colIndex) => {
              const field = columnMap[colIndex];
              const cellAddress = XLSX.utils.encode_cell({
                r: rowNum,
                c: parseInt(colIndex),
              });
              const cell = worksheet[cellAddress];
              const value =
                cell && cell.v !== undefined ? String(cell.v).trim() : "";
              rowData[field] = value;
              if (value !== "") hasData = true;
            });

            if (hasData) {
              allRows.push(rowData);
            }
          }
          console.log("Extracted rows:", allRows);
          setTotalRows(allRows.length);

          const incomplete = [];
          const complete = [];
          const phoneNumbers = [];

          allRows.forEach((rowData) => {
            const row = {
              user_name: rowData["User Name"] || "",
              user_mobile_no: rowData["User Mobile Number"]
                ? `+91${rowData["User Mobile Number"]}`
                : "",
              perTxnLimit: rowData["Per Transaction Limit"]
                ? Number(rowData["Per Transaction Limit"])
                : 0,
              dailyLimit: rowData["Daily Limit"]
                ? Number(rowData["Daily Limit"])
                : 0,
              monthlyLimit: rowData["Monthly Limit"]
                ? Number(rowData["Monthly Limit"])
                : 0,
              isAdmin: rowData["isAdmin"]?.toLowerCase() === "yes",
              rowNumber: rowData.rowNumber,
              user_email: rowData["User Email"] || "",
              tags: rowData["Tags"] || "",
            };

            // Use the component's validateUser function
            const issues = validateUser(row);
            console.log(`Row ${row.rowNumber} issues:`, issues);

            if (issues.length > 0) {
              incomplete.push({ ...row, issues });
            } else {
              complete.push(row);
              phoneNumbers.push(row.user_mobile_no);
            }
          });

          // Check duplicates
          const duplicates = checkDuplicates(complete);
          const finalComplete = complete.filter(
            (row) =>
              !duplicates.some(
                (dup) => dup.user_mobile_no === row.user_mobile_no
              )
          );

          // Check existing users
          let existingUsers = finalComplete;
          if (phoneNumbers.length > 0) {
            const UserExist = await getUserExists(phoneNumbers);
            existingUsers = finalComplete.filter((row) => {
              if (UserExist.includes(row.user_mobile_no)) {
                incomplete.push({
                  ...row,
                  issues: ["User already exists"],
                });
                return false;
              }
              return true;
            });
          }

          console.log("Complete rows:", complete);
          console.log("Incomplete rows:", incomplete);
          console.log("Duplicates:", duplicates);
          console.log("Final complete data:", existingUsers);

          setDuplicateUsers(duplicates.length);
          setIncompleteRows([...incomplete, ...duplicates]);
          setCompleteData(existingUsers);
          console.log("dataStored==============");
        } catch (parseError) {
          console.log("Error parsing file:", parseError);
          setError(true);
        }
      };

      if (file.type === "text/csv") {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      console.log("File upload error:", error);
      setError(true);
    }
  };

  const getUserExists = async (data) => {
    try {
      let existsUsers = [];
      let tokens = localStorage.getItem("authToken");
      await verifyTokenAccess("Bult User getUserExists")
      let parsedTokens = JSON.parse(tokens);
      const expired = isTokenExpired(parsedTokens.IdToken);
      if (expired) {
        parsedTokens = getNewTokens(parsedTokens);
      }

      let obj = {
        ledger_label: store.Company_Id,
        user_mobile_no_arr: data,
      };
      let payload = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${config.node_api_endpoint}/cupi/admin/verifyUsersExists`,
        headers: {
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
          "Content-Type": "application/json",
        },
        data: obj,
      };
      const res = await axios.request(payload);
      existsUsers = res.data.data.users_already_registered || [];
      return existsUsers;
    } catch (err) {
      console.log("getUserExists error:", err);
      return [];
    }
  };

const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);

      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
        return decoded.exp < currentTime;
      }
    } catch (error) {
      console.log("Error decoding token:", error);
      return true; // Treat decoding failures as expired tokens
    }
    return true;
  };


  const getNewTokens = async (authData) => {
    if (!authData?.RefreshToken) {
      localStorage.removeItem("authToken");
      navigate("/login");
      return;
    }

    try {
      const client = new CognitoIdentityProviderClient({
        region: "ap-south-1",
      });

      const params = {
        ClientId: config.Client_Id,
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: {
          REFRESH_TOKEN: authData?.RefreshToken,
        },
      };

      const command = new InitiateAuthCommand(params);
      const refreshResponse = await client.send(command);

      if (refreshResponse.AuthenticationResult) {
        const newAuthToken = {
          AccessToken: refreshResponse.AuthenticationResult.AccessToken,
          RefreshToken: authData?.RefreshToken, // Keep old refresh token
          IdToken: refreshResponse.AuthenticationResult.IdToken,
        };

        localStorage.setItem("authToken", JSON.stringify(newAuthToken));
        if (store.refresh) {
          store.setRefresh(false);
        } else {
          store.setRefresh(true);
        }
      } else {
        localStorage.removeItem("authToken");
        navigate("/login");
      }
    } catch (error) {
      console.log("Error refreshing tokens:", error);
      localStorage.removeItem("authToken");
      navigate("/login");
    }
  };
  
  const InValidRows = () => {
    const headers = [
      "Name",
      "Email",
      "Mobile No",
      "Per TxN Limit",
      "Daily Limit",
      "Monthly Limit",
      "Admin",
      "Tags",
      "Reason for error",
    ];

    if (!Array.isArray(incompleteRows)) {
      console.log("incompleteRows is not an array!");
      return;
    }

    const dataRows = incompleteRows.map((row) => [
      row.user_name || "-",
      row.user_email || "-",
      row.user_mobile_no || "-",
      row.perTxnLimit || "-",
      row.dailyLimit || "-",
      row.monthlyLimit || "-",
      row.isAdmin ? "Yes" : "No",
      row.tags || "-",
      row.issues?.join(", ") || "-",
    ]);

    const excelData = [headers, ...dataRows];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invalid Users");
    XLSX.writeFile(workbook, "invalid_users.xlsx");
  };

  const sortedData = useMemo(() => {
    return [...incompleteRows].sort(
      (a, b) => (a?.rowNumber || 0) - (b?.rowNumber || 0)
    );
  }, [incompleteRows]);

  const startIndex = (currentPage - 1) * pageSize;
  const currentPageData = sortedData.slice(startIndex, startIndex + pageSize);

  const handlePaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const sendData = async () => {
    let tokens = localStorage.getItem("authToken");
    await verifyTokenAccess("Bulk User send Data")
    let parsedTokens;
    try {
      parsedTokens = JSON.parse(tokens);
      const expired = isTokenExpired(parsedTokens.IdToken);
      if (expired) {
        parsedTokens = getNewTokens(parsedTokens);
      }

    } catch (err) {
      console.log("Invalid auth token:", err);
      return;
    }

    let failedUsers = [];
    let successfulUsers = [];

    // Check duplicates
    const duplicates = checkDuplicates(completeData);
    failedUsers = [...duplicates];

    // Filter out duplicates
    const uniqueUsers = completeData.filter(
      (row) =>
        !duplicates.some((dup) => dup.user_mobile_no === row.user_mobile_no)
    );

    for (let user of uniqueUsers) {
      const issues = validateUser(user);
      if (issues.length > 0) {
        failedUsers.push({ ...user, issues });
        continue;
      }

      const {
        user_name,
        user_email,
        user_mobile_no,
        perTxnLimit,
        dailyLimit,
        monthlyLimit,
        isAdmin,
        tags,
      } = user;

      let obj1;
      if (isAdmin) {
        obj1 = JSON.stringify({
          user_name: user_mobile_no.trim(),
          user_full_name: user_name.trim(),
          user_mobile_no: user_mobile_no.trim(),
          ...(user_email && { user_email: user_email.trim() }),
          perTxnLimit: Number(perTxnLimit),
          dailyLimit: Number(dailyLimit),
          monthlyLimit: Number(monthlyLimit),
          ledger_label: store.Company_Id,
          roles: ["CUPI_COMPANY_EMPLOYEE", "ADMIN"],
          tags,
        });
      } else {
        obj1 = JSON.stringify({
          user_name: user_mobile_no.trim(),
          user_full_name: user_name.trim(),
          user_mobile_no: user_mobile_no.trim(),
          ...(user_email && { user_email: user_email.trim() }),
          perTxnLimit: Number(perTxnLimit),
          dailyLimit: Number(dailyLimit),
          monthlyLimit: Number(monthlyLimit),
          ledger_label: store.Company_Id,
          roles: ["CUPI_COMPANY_EMPLOYEE"],
          tags,
        });
      }

      const payload = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${config.node_api_endpoint}/cupi/admin/create_cupi_compnay_user`,
        headers: {
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
          "Content-Type": "application/json",
        },
        data: obj1,
      };

      try {
        const res = await axios.request(payload);
        if (res.data.data.msg.message.trim() === "User Already Exists :") {
          failedUsers.push({
            ...user,
            issues: ["User already exists"],
          });
          console.log(`User ${user_name} already exists`);
        } else {
          successfulUsers.push(user);
          console.log(`User ${user_name} added successfully`);
        }
      } catch (err) {
        console.log(`API error for user ${user_name}:`, err);
        let errorMessage = "Failed to add user";
        if (err.response && err.response.status === 400) {
          errorMessage = "User already exists";
        }
        failedUsers.push({
          ...user,
          issues: [errorMessage],
        });
      }
    }

    // Update states
    setCompleteData(successfulUsers);
    setIncompleteRows((prev) => [...prev, ...failedUsers]);
    setDuplicateUsers((prev) => prev + failedUsers.length);
    setDataUpdated(true);
  };

  const handleReuploadClick = () => {
    fileInputRef.current.click();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileType = file.name.split(".").pop().toLowerCase();
      if (["csv", "xlsx", "xls"].includes(fileType)) {
        setError(false);
        handleFileUpload({ target: { files: [file] } });
      } else {
        setError(true);
      }
    }
  };

  if (totalRows <= 0) {
    return (
      <div className={styles.BulkUserContainer}>
        <div className={styles.BulkUserTemplate}>
          <button
            className={styles.BulkUserButton1}
            onClick={() => navigate("/content/users")}
          >
            <img
              className={styles.plusCircleIcon1}
              alt=""
              src="/arrownarrowleft.svg"
            />
            Back
          </button>
          <button className={styles.BulkUserButton} onClick={downloadExcel}>
            <img
              className={styles.plusCircleIcon}
              alt=""
              src="/DownloadIcon.svg"
            />
            User Template
          </button>
        </div>
        <div
          className={`${styles.BulkUserupload} ${
            isDragging ? styles.dragging : ""
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className={styles.BulkUseruploadContainer}>
            <div className={styles.BulkUseruploadContainer1}>
              <img
                className={styles.plusCircleImage}
                alt=""
                src="/PlaceholderImage.svg"
              />
              <div className={styles.BulkUseruploadContainerText}>
                + Upload only Excel Format document
              </div>
            </div>
            <div className={styles.BulkUseruploadContainerLink}>
              <img
                className={styles.plusCircleIcon}
                alt=""
                src="/UploadIcon.svg"
              />
              <div className={styles.plusCircleSpanCOnatiner}>
                <span className={styles.plusCircleSpan}>
                  Drag & drop the file here or
                </span>
                <input
                  className={styles.plusCircleButton}
                  type="file"
                  accept=".csv, .xlsx, .xls"
                  onChange={(e) => {
                    setError(false);
                    const file = e.target.files[0];
                    if (file) {
                      const fileType = file.name.split(".").pop().toLowerCase();
                      if (["csv", "xlsx", "xls"].includes(fileType)) {
                        handleFileUpload(e);
                      } else {
                        setError(true);
                      }
                    }
                  }}
                  style={{ display: "block"}}
                />
              </div>
            </div>
            {error && (
              <div className={styles.BulkUseruploadContainerError}>
                <img
                  className={styles.plusCircleIcon}
                  alt=""
                  src="/x-circle.svg"
                />
                Please upload correct file format.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className={styles.BulkUserTableContainer}>
        <div className={styles.BulkUserTableContainer}>
          <div className={styles.plusCircleInfoContainer}>
            <div>
              <img
                className={styles.plusCircleIconHeading}
                alt=""
                src="/person-vcard-placeholder.svg"
              />
            </div>
            <div className={styles.plusCircleInfoContainer2}>
              <div className={styles.plusCircleInfo1}>{fullData?.length}</div>
              <div className={styles.plusCircleInfoP}>Total users uploaded</div>
            </div>
            <div className={styles.BulkUserTableHeadingBorder}></div>
            <div className={styles.plusCircleInfoContainer2}>
              <div className={styles.plusCircleInfo1}>
                {completeData?.length}
              </div>
              <div className={styles.plusCircleInfoP}>
                User details are valid
              </div>
            </div>
            <div className={styles.BulkUserTableHeadingBorder}></div>
            <div className={styles.plusCircleInfoContainer2}>
              <div className={styles.plusCircleInfo2}>
                {incompleteRows?.length}
              </div>
              <div className={styles.plusCircleInfoP}>
                User details have errors
              </div>
            </div>
          </div>
          <div className={styles.BulkUserTableHead1}>
            <div className={styles.BulkUserTableHead}>
              <div className={styles.BulkUserTableHeadng}>
                <div className={styles.BulkUserTableHeadngH1}>
                  Failed User Details
                </div>
              </div>
              <div className={styles.BulkUserTableButton}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                />
              </div>
            </div>
            <table className={styles.table}>
              <thead className={styles.tableHead}>
                <tr className={styles.tableHeaderRow}>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>ROW</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>NAME</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>EMAIL</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>MOBILE NO</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>PER TXN LIMIT (₹)</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>DAILY LIMIT(₹)</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>MONTHLY LIMIT(₹)</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>ADMIN</div>
                  </th>
                  <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>TAGS</div>
                  </th>
                  {/* <th className={styles.tableHeaderRowCell}>
                    <div className={styles.head}>Reason for error</div>
                  </th> */}
                </tr>
              </thead>
              <tbody className={styles.tableHead}>
                {console.log("invalid rows-->", incompleteRows)}

                {currentPageData.map((detail, index) => {
                  const isIncomplete = !!detail.issues;
                  return (
                    <tr
                      key={index}
                      className={
                        detail.issues?.includes("User already exists")
                          ? styles.InvalidtableBodyrow
                          : styles.tableBodyrow
                      }
                    >
                      <td className={styles.tableBodydata}>
                        <div
                          className={isIncomplete ? styles.data3 : styles.data}
                        >
                          {detail.rowNumber}
                        </div>
                      </td>
                      <td className={styles.tableBodydata}>
                        <div
                          className={
                            isValidUsername(detail.user_name) &&
                            !detail.issues?.includes("Missing Entries") &&
                            !detail.issues?.includes(
                              "user_name: Must be at least 5 characters"
                            ) &&
                            !detail.issues?.includes("User already exists")
                              ? styles.data1
                              : styles.data3
                          }
                        >
                          {detail.user_name || "-"}
                        </div>
                      </td>
                      <td className={styles.tableBodydata}>
                        <div
                          className={
                            isValidEmail(detail.user_email) &&
                            !detail.issues?.includes("Wrong Email Format") &&
                            !detail.issues?.includes("User already exists")
                              ? styles.data1
                              : styles.data3
                          }
                        >
                          {detail.user_email || "-"}
                        </div>
                      </td>
                      <td className={styles.tableBodydata}>
                        <div
                          className={
                            isValidMobileNo(
                              detail.user_mobile_no?.replace("+91", "")
                            ) &&
                            !detail.issues?.includes("Wrong Ph num Format") &&
                            !detail.issues?.includes("Missing Entries") &&
                            !detail.issues?.includes("User already exists")
                              ? styles.data1
                              : styles.data3
                          }
                        >
                          {detail.user_mobile_no || "-"}
                        </div>
                      </td>
                      <td className={styles.tableBodydata}>
                        <div
                          className={
                            !detail.issues?.includes(
                              "Txn limits cant be empty or 0"
                            ) &&
                            !detail.issues?.includes(
                              "Per txn cant be more than Daily"
                            ) &&
                            !detail.issues?.includes(
                              "Per txn cant be more than Monthly"
                            ) &&
                            !detail.issues?.includes(
                              "Per txn limit exceeds 1 lakh"
                            ) &&
                            !detail.issues?.includes("User already exists")
                              ? styles.data
                              : styles.data3
                          }
                        >
                          {detail.perTxnLimit || "-"}
                        </div>
                      </td>
                      <td className={styles.tableBodydata}>
                        <div
                          className={
                            !detail.issues?.includes(
                              "Daily limit cant be empty or 0"
                            ) &&
                            !detail.issues?.includes(
                              "Daily txn cant be more than Monthly"
                            ) &&
                            !detail.issues?.includes(
                              "Daily txn limit exceeds 1 lakh"
                            ) &&
                            !detail.issues?.includes("User already exists")
                              ? styles.data
                              : styles.data3
                          }
                        >
                          {detail.dailyLimit || "-"}
                        </div>
                      </td>
                      <td className={styles.tableBodydata}>
                        <div
                          className={
                            !detail.issues?.includes(
                              "Monthly limit cant be empty or 0"
                            ) && !detail.issues?.includes("User already exists")
                              ? styles.data
                              : styles.data3
                          }
                        >
                          {detail.monthlyLimit || "-"}
                        </div>
                      </td>
                      <td className={styles.tableBodydata}>
                        <div
                          className={
                            !detail.issues?.includes("You are not authorised to add Admin users.") &&
                            !detail.issues?.includes("User already exists")
                              ? styles.data
                              : styles.data3
                          }
                        >
                          {detail.isAdmin ? "Yes" : "No"}
                        </div>
                      </td>
                      <td className={styles.tableBodydata}>
                        <div className={styles.data}>{detail.tags || "-"}</div>
                      </td>
                      {/* <td className={styles.tableBodydata}>
                        <div className={styles.data}>
                          {detail.issues && detail.issues.length > 0
                            ? `${detail.issues.join(", ").slice(0, 15)}...`
                            : "-"}
                        </div>
                      </td> */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className={styles.bottom}>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={incompleteRows.length}
                onChange={handlePaginationChange}
              />
            </div>
          </div>
        </div>
        <div className={styles.import_Button_Container}>
          <button
            className={styles.import_Button2}
            onClick={handleReuploadClick}
          >
            Reupload Template
          </button>
          <button className={styles.import_Button} onClick={sendData}>
            Continue
          </button>
        </div>
        {dataUpdated && (
          <div className={styles.Notification}>
            <BulkNortification
              isVisible={dataUpdated}
              completeData={completeData.length}
              incompleteData={incompleteRows.length}
              duplicateUsers={duplicateUsers}
              onClose={() => setDataUpdated(false)}
            />
          </div>
        )}
      </div>
    );
  }
};

export default BulkUsers;
